/* eslint-disable max-lines */
import React, {useEffect} from 'react';
import {RouteComponentProps, RouteProps, useHistory} from 'react-router';
import {Route as ReactRouterRoute, Switch, Redirect} from 'react-router-dom';
import {
    calculateDuration,
    getAdditionalPerformanceInfo,
    pushDataLayer,
    createGAEvent,
    PERFORMANCE,
} from '@optimaxdev/analytics/mobile';
import {Location} from 'history';
import loadable from '@loadable/component';
import {withSentryRouting, withProfiler} from '@sentry/react';
import {Rox} from 'rox-ssr';

import {DynamicPathResolverConnected} from 'components/dynamicPathResolver/dynamicPathResolver';
import {withSuspense, LoaderComponent} from 'components/__helpers/withSuspense/withSuspense';
import {LANDING_PAGES} from 'libs/roxCustomSetup';
import {Account} from 'router/customer/router';
import {CartSkeletonConnected} from '@page/cart/components/cartSkeleton/cartSkeleton';
import {CheckoutSkeleton} from '@page/checkout/components/checkoutSkeleton/checkoutSkeleton';
import {getCookie} from 'libs/getCookie';
import {logWithSentry} from 'libs/sentry/sentry';
/**
 * withSentryRouting should be before the withProfiler, because it creates transactions on
 * route changes which will be passed to the Sentry's Profiler
 */
const Route = withSentryRouting(withProfiler(ReactRouterRoute, {name: 'AppRouter'}));

const ProfileConnectedDynamic = loadable(() =>
    import(/* webpackChunkName: "ProfileConnectedDynamic" */ 'pages/profile/profile'),
);

const CustomerReviewDynamic = loadable(() =>
    import(
        /* webpackChunkName: "CustomerReview" */ 'pages/profile/components/customerReview/customerReview'
    ),
);

const SalesRouter = loadable(() => import(/* webpackChunkName: "SalesRouter" */ './sales/router'));

const BlogRouter = loadable(() => import(/* webpackChunkName: "BlogRouter" */ './blog/router'));

const Wishlist = loadable(() =>
    import(/* webpackChunkName: "WishlistPage" */ 'pages/wishlist/wishlist'),
);

const NewBlogConnected = loadable(() => import(/* webpackChunkName: "blog" */ 'pages/blog/blog'));
const Home = loadable(() => import(/* webpackChunkName: "home" */ 'pages/home/index'), {
    fallback: LoaderComponent,
});
const QuickStartGuide = loadable(
    () => import(/* webpackChunkName: "quickStartGuide" */ 'pages/startGuide/quickStartGuide'),
    {fallback: LoaderComponent},
);
const SuccessPageConnected = loadable(() =>
    import(/* webpackChunkName: "successPage" */ 'pages/success'),
);
const CheckoutAppDynamic = loadable(
    () => import(/* webpackChunkName: "checkoutapp" */ 'pages/checkout/checkoutApp'),
    {fallback: <CheckoutSkeleton />},
);
const Cart = loadable(() => import(/* webpackChunkName: "cart" */ 'pages/cart/cart'), {
    fallback: <CartSkeletonConnected />,
});
const SearchResults = withSuspense(
    React.lazy(() =>
        import(/* webpackChunkName: "searchresults" */ 'containers/searchResults/searchResults'),
    ),
);
const SixOverSix = withSuspense(
    React.lazy(() =>
        import(/* webpackChunkName: "sixoversix" */ 'containers/sixOverSix/sixOverSix'),
    ),
);
const Configure = withSuspense(
    React.lazy(() => import(/* webpackChunkName: "configure" */ 'containers/configure/configure')),
);

const OrderTracking = withSuspense(
    React.lazy(() =>
        import(/* webpackChunkName: "orderTracking" */ 'pages/orderTracking/orderTracking'),
    ),
);
const AddReview = withSuspense(
    React.lazy(() => import(/* webpackChunkName: "addreview" */ 'pages/addReview/addReview')),
);
const Payment = withSuspense(
    React.lazy(() => import(/* webpackChunkName: "payment" */ 'containers/payment/payment')),
);
const PostPurchaseInsurance = loadable(
    () =>
        import(
            /* webpackChunkName: "PostPurchaseInsurance" */ 'pages/postPurchaseInsurance/postPurchaseInsurance'
        ),
    {fallback: LoaderComponent},
);
const RmaConnected = withSuspense(
    React.lazy(() => import(/* webpackChunkName: "Rma" */ 'containers/rma/rma')),
);
const Contacts = withSuspense(
    React.lazy(() => import(/* webpackChunkName: "Contacts" */ 'containers/contacts/contacts')),
);

const QuizConnected = loadable(
    () => import(/* webpackChunkName: "Quiz" */ 'pages/quiz/quiz'),
    'default',
    {
        fallback: LoaderComponent,
    },
);

/**
 * Wrapper on route to allow nodejs server understand if it is known route or not
 * Temporary workaround: if it is rendered by server, it is done with StaticRouter which passes staticContext prop
 * In this case we define context statusCode (200 by default) and return null, as now we do not have to render on server
 * In case if it is rendered by client staticContext is not defined and everything goes on as usual
 * @return {Node} wrapper on route
 */
const ContextRoute = ({
    component: Component,
    render,
    code = 200,
    page = '',
    ...props
}: RouteProps & {code?: number, page?: string}) => (
    <Route
        {...props}
        render={({staticContext, ...routeProps}: RouteComponentProps): React.ReactNode => {
            if (staticContext && staticContext.statusCode) {
                // eslint-disable-next-line no-param-reassign
                staticContext.statusCode = code;
                // eslint-disable-next-line no-param-reassign
                if (page) staticContext.page = page;
                return null;
            }
            if (Component) return <Component {...routeProps} />;
            if (render) return render(routeProps);
            return null;
        }}
    />
);

type PropsType = {
    location: Location,
};

/**
 * List of known routes + one for all unknown
 * @return {React.ReactElement} list of routes
 */
export const Routes: React.FC<PropsType> = ({location}: PropsType) => {
    const history = useHistory();

    useEffect(() => {
        try {
            const buildVersion = getCookie('buildVersion');
            if (buildVersion) {
                Rox.setCustomStringProperty('appBuild', buildVersion);
            }
        } catch (error) {
            logWithSentry(error);
        }
    }, [location?.pathname]);

    // eslint-disable-next-line max-statements, complexity
    useEffect(() => {
        const urlSearchParams = new URLSearchParams(location?.search);
        const deepLink = urlSearchParams.get('deep_link_value');
        if (deepLink) {
            urlSearchParams.delete('deep_link_value');
            let deepLinkUrl = '/';
            switch (deepLink) {
                case 'cart':
                    deepLinkUrl = '/checkout/cart';
                    break;
                case 'account':
                    deepLinkUrl = '/customer/account';
                    break;
                case 'wishlist':
                    deepLinkUrl = '/wishlist';
                    break;
                case 'hp':
                default:
                    break;
            }
            if (deepLink.startsWith('ctp/') || deepLink.startsWith('/ctp/')) {
                deepLinkUrl = deepLink.replace('ctp/', '');
            }
            if (deepLink.includes('pdp/') || deepLink.startsWith('/pdp/')) {
                deepLinkUrl = deepLink.replace('pdp/', '');
            }
            history.push(`${deepLinkUrl}?${urlSearchParams.toString()}`);
        }
    }, [location?.search]);

    return (
        <Switch location={location}>
            <Redirect from="/paypal/express/cancel" to="/checkout/cart" />
            <Route exact path="/" component={Home} />
            <ContextRoute path={['/pairfect_match', '/quiz-glasses']} component={QuizConnected} />

            <ContextRoute
                exact
                path="/optimaxcheckout/amazon"
                component={CheckoutAppDynamic}
                page={LANDING_PAGES.checkout}
            />
            <ContextRoute
                path="/sales"
                render={() => (
                    <ProfileConnectedDynamic>
                        <SalesRouter />
                    </ProfileConnectedDynamic>
                )}
            />
            <ContextRoute path="/customer/account" render={() => <Account location={location} />} />
            <ContextRoute exact path="/review/customer" component={CustomerReviewDynamic} />
            <ContextRoute exact path="/salesperson/result" component={SearchResults} />
            <ContextRoute exact path="/scan" component={SixOverSix} />
            <ContextRoute exact path="/contacts" component={Contacts} />
            <ContextRoute exact path="/rma" component={RmaConnected} />
            <ContextRoute
                exact
                path="/blog/2021/:postUrl"
                render={({match}) => <NewBlogConnected slug={match.params.postUrl} />}
            />
            <ContextRoute path="/blog" component={BlogRouter} />
            <ContextRoute
                exact
                path="/checkout/onepage"
                component={CheckoutAppDynamic}
                page={LANDING_PAGES.checkout}
            />
            <ContextRoute exact path="/checkout/cart" component={Cart} page={LANDING_PAGES.cart} />
            <ContextRoute exact path="/checkout/onepage/success" component={SuccessPageConnected} />
            <ContextRoute
                exact
                path="/tracking/search/:orderNumber?"
                render={(): Node => <OrderTracking />}
            />
            <ContextRoute
                exact
                path="/home-try-on/payment/:order"
                render={({match}) => <Payment {...match.params} />}
            />
            <ContextRoute
                exact
                path="/review/product/list/id/:id"
                render={({match}) => <AddReview params={match.params} />}
            />
            <ContextRoute
                exact
                path="/checkout/cart/configure/id/:quoteId"
                render={({match}) => <Configure page="configure" quoteId={match.params.quoteId} />}
            />
            <ContextRoute
                exact
                path="/limited-time-offer-enhanced-coverage/:orderId/"
                render={({match}) => <PostPurchaseInsurance orderId={match.params.orderId} />}
            />
            <ContextRoute exact path="/wishlist" component={Wishlist} />
            <ContextRoute exact path="/start-guide" component={QuickStartGuide} />
            <ContextRoute code={404} component={DynamicPathResolverConnected} />
        </Switch>
    );
};

/**
 * React Router
 * @returns {React.ReactElement} Return current route
 */
export const Router: React.FC<PropsType> = ({location}: PropsType) => {
    useEffect(() => {
        if (window.performance && typeof window.performance.mark === 'function') {
            window.performance.mark('endRenderTask');
            calculateDuration(
                (duration: number) => {
                    if (duration)
                        pushDataLayer({
                            ...createGAEvent(
                                PERFORMANCE,
                                {
                                    label: String(duration),
                                    name: 'App Load',
                                },
                                false,
                            ),
                            ...getAdditionalPerformanceInfo(),
                        });
                },
                {start: 'startRenderTask', end: 'endRenderTask'},
            );
        }
        // eslint-disable-next-line no-underscore-dangle
        window.__showRolloutFlags = Rox.showOverrides;
    }, []);

    return <Routes location={location} />;
};
